"use client";
import React, {
    forwardRef,
    useCallback,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from "react";
import mainStyle from "./filterNavBar.module.scss";
import classnames from "classnames";
import { useFilterConditionContext, usePageInfoContext } from "@/src/provider";
import { useProductsContext } from "@/src/provider/Products/context";
import { useFetchProducts } from "@/src/provider/Products/hooks";
const BackToTop = dynamic(() => import("../BackToTop"));
import dynamic from "next/dynamic";
import { useTranslation } from "@/src/i18n/client";
import FilterModalNew from "../FilterModalNew";
import { changeView, enforceZIndexUnderCurrentId } from "./const";
import useOptimizedScroll from "@/src/hooks/useOptimizedScroll";
import { FmIcon, FmSkeleton } from "@/ui-component";
import { EIconType } from "@/public/static/interface";
import { useHeaderStore } from "@/store/pageModules/header";
export interface IProps {
    noNav: boolean;
}

const showIcon: Record<string, EIconType> = {
    single: "icon--switch2",
    dubble: "icon--switch1",
};

const FilterNavBarNew = forwardRef(function FilterNavBar(props: IProps, ref) {
    const { noNav } = props;
    const [showGuide, setShowGuide] = useState<boolean>(true);
    const [isShowBackToTop, setIsShowBackToTop] = useState<boolean>(false);
    const [current, setCurrent] = useState<number>(0);
    const [fixedHeaderBar, setFixedHeaderBar] = useState<boolean>(false);
    const [isShowFilterModal, setIsShowFilterModal] = useState<boolean>(false);
    const filterNavConfig = useFilterConditionContext(
        (state) => state.filterNavConfig,
    );

    const [ot, setOt] = useState(175);
    const [total, isShowSkeleton, isLoading] = useProductsContext((state) => [
        state.productData.pagination.total,
        state.isShowSkeleton,
        state.isLoading,
    ]);
    const [singleCol, updateProductsListLayout] = usePageInfoContext(
        (state) => [state.isSingleCol, state.updateProductsListLayout],
    );
    const [setHeaderOptions, fixedHeader] = useHeaderStore((state) => [
        state.setHeaderOptions,
        state.fixedHeader,
    ]);
    const { t } = useTranslation(["specialSub"]);
    const filterCount = useMemo(
        () => filterNavConfig.filter((item) => item.hasVal).length,
        [filterNavConfig],
    );
    // 该ref滚动到触底，则zIndex的高度取代header成为最高层级，往下滑动非触底时，则成为普通层级
    const filterNavBarRef = useRef<HTMLDivElement>(null);

    const updateIsSingleCol = useCallback(
        (changeSingle: boolean) => {
            updateProductsListLayout(changeSingle);
        },
        [updateProductsListLayout],
    );

    // 回到第一个商品的位置
    const backTop = useCallback(() => {
        self.scrollTo(0, ot - 50);
        setIsShowBackToTop(false);
    }, [ot]);
    // 刷新

    const fetchProductsRes = useFetchProducts();
    const refresh = useCallback(() => {
        backTop();
        // 给url加上筛选后的条件
        fetchProductsRes("filter", { page: 1 });
    }, [backTop, fetchProductsRes]);

    const handleScroll = useCallback(() => {
        if (!filterNavBarRef.current) return;
        // 计算筛选栏离顶部的高度
        const bottomNoticeHeight =
            document.getElementById("bottom-notice-container")?.offsetHeight ??
            0;

        const headerHeight =
            document.getElementById(enforceZIndexUnderCurrentId)
                ?.offsetHeight ?? 0;

        const bannerHeight =
            document.getElementById("banner-container")?.offsetHeight ?? 0;
        const filterNavOffsetTop =
            bottomNoticeHeight + headerHeight + bannerHeight;

        // 初始化有位置导致获取高度不正确，滚动时进行判断，将其更新
        if (ot !== filterNavOffsetTop) {
            setOt((prev) => filterNavOffsetTop ?? prev);
        }

        if (
            filterNavBarRef.current &&
            window.scrollY >= filterNavOffsetTop - 1
        ) {
            setFixedHeaderBar(true);
        } else {
            setFixedHeaderBar(false);
        }

        const currentScrollY = window.scrollY;
        if (currentScrollY > lastScrollY.current) {
            // 下划
            setShowGuide(false);
        } else {
            // 上划
            setShowGuide(true);
        }

        lastScrollY.current = currentScrollY;
        //滑到第三屏显示回到顶部按钮
        if (
            currentScrollY >= ot + document.documentElement.clientHeight * 3 &&
            !isShowBackToTop
        ) {
            setIsShowBackToTop(true);
        }

        //隐藏顶部按钮
        if (
            currentScrollY < ot + document.documentElement.clientHeight * 3 &&
            isShowBackToTop
        ) {
            setIsShowBackToTop(false);
        }

        let fixedElementsHeight = 0;

        const filter = document.querySelector("#filterNavBar");
        if (!filter) {
            return;
        }
        const filterNav = window.getComputedStyle(filter);
        const filterHeight = filterNav.getPropertyValue("height");
        fixedElementsHeight += attrVal2num(filterHeight);

        const topNav = document.querySelector(".app-bar-container");
        if (!topNav) {
            return;
        }
        const topNavStyle = window.getComputedStyle(topNav);
        const topNavHeight = topNavStyle.getPropertyValue("height");
        fixedElementsHeight += attrVal2num(topNavHeight);

        const list = document.querySelectorAll(".productItem");

        let t = 0;
        for (let j = 0, k = list.length; j < k; j++) {
            const { top, bottom } = list[j].getBoundingClientRect();

            if (top > fixedElementsHeight && bottom <= window.innerHeight) {
                t = j + 1;
                break;
            }
        }
        setCurrent(t * 2);
    }, [isShowBackToTop, ot]);

    useOptimizedScroll({ onScroll: handleScroll });

    const attrVal2num = (str: string) => {
        return parseFloat(str.substring(0, str.length - 2));
    };

    // 筛选modal显示
    const toggleFilterModal = () => {
        setIsShowFilterModal(!isShowFilterModal);
    };
    const containerClass = useMemo(() => {
        return classnames(mainStyle["filter-nav-bar"], {
            [mainStyle["filter-nav-bar-fixed"]]: !showGuide || singleCol,
            [mainStyle["filter-nav-bar-two-nav"]]:
                showGuide && fixedHeaderBar && !noNav && !singleCol,
        });
    }, [fixedHeaderBar, noNav, showGuide, singleCol]);
    const lastScrollY = useRef<number>(0);
    useImperativeHandle(ref, () => {
        return filterNavBarRef.current;
    }, []);

    const handleChangeIconCol = (e: any) => {
        window.scrollTo(0, 0);
        const isSingle = e.target.className.includes(showIcon["single"]);
        updateIsSingleCol(isSingle);
        setFixedHeaderBar(false);
        setHeaderOptions([{ fixedHeader: false }]);
    };

    return (
        <>
            <div
                id="filterNavBarContainer"
                className={classnames(mainStyle["filter-nav-main"])}
                ref={filterNavBarRef}
            >
                <div
                    id="filterNavBar"
                    className={classnames(
                        mainStyle["filter-nav-bar"],
                        containerClass,
                    )}
                    style={
                        fixedHeaderBar && fixedHeader
                            ? { position: "fixed" }
                            : { zIndex: 9 }
                    }
                >
                    <div
                        className={mainStyle["sort-filter"]}
                        onClick={toggleFilterModal}
                    >
                        <FmIcon
                            icon={"icon--filter"}
                            className={classnames(mainStyle["filterIcon"])}
                        />
                        {/* TODO Sort的多语言 */}
                        {`${t("specialSub:Filter")} & ${t("specialSub:Sortby")}`}
                        {filterCount > 0 && <span
                            className={classnames({
                                [mainStyle["filter-number"]]: true,
                                [mainStyle["active"]]: filterCount > 0,
                            })}
                        >
                            {filterCount}
                        </span>}
                    </div>
                    <div className={mainStyle["results-switch"]}>
                        {/* <div className={mainStyle["results"]}>
                            {isShowSkeleton && isLoading ? (
                                <FmSkeleton
                                    variant="rounded"
                                    component="span"
                                    width={"0.4rem"}
                                    height={"0.3rem"}
                                />
                            ) : (
                                total
                            )}
                            &nbsp;Results
                        </div> */}
                         {/* 关闭双列表切换*/}
                        {/* <div
                            className={classnames(
                                mainStyle["choose-show-col-icon"],
                                singleCol
                                    ? mainStyle["single-col"]
                                    : mainStyle["dubble-col"],
                            )}
                            onClick={handleChangeIconCol}
                        >
                            <FmIcon
                                icon={showIcon["single"]}
                                className={mainStyle["show-single"]}
                            />
                            <FmIcon
                                icon={showIcon["dubble"]}
                                className={mainStyle["show-dubble"]}
                            />
                        </div> */}
                    </div>
                </div>
            </div>
            <FilterModalNew
                open={isShowFilterModal}
                onClose={toggleFilterModal}
                refresh={refresh}
            />
            {isShowBackToTop && (
                <BackToTop
                    visible={isShowBackToTop}
                    backToTop={backTop}
                    total={total}
                    current={current}
                />
            )}
        </>
    );
});

export default FilterNavBarNew;
